import { useState, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Phone, Mail, MapPin, Clock, Send, Check } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { API_CONFIG } from '../config/api'
import { Helmet } from 'react-helmet-async'
import JsonLd from '../components/JsonLd'
import { publicUrl } from '../utils/publicUrl'

export default function Contact() {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const packageName = searchParams.get('package') || ''
  const packageSlug = searchParams.get('slug') || ''
  const packageId = searchParams.get('package_id') || ''
  const packageUrl = packageSlug
    ? `${window.location.origin}/packages/${packageSlug}`
    : ''

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pax: '',
    dates: '',
    origin: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const { franchiseData, loading } = useFranchiseData()
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaWidgetId = useRef<number | null>(null)
  const recaptchaDiv = useRef<HTMLDivElement | null>(null)

  const SITE_KEY = '6LdrzLosAAAAAJKBYY18RRKQMc0PJ3VHrW6ou7DR'

  // Load the standard reCAPTCHA v2 script once on mount
  useEffect(() => {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return

    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
    script.async = true
    document.head.appendChild(script)
  }, [])

  // Render the widget when the div mounts
  const recaptchaRefCallback = useCallback((node: HTMLDivElement | null) => {
    recaptchaDiv.current = node
    if (!node) return

    if (recaptchaWidgetId.current !== null) return

    const tryRender = () => {
      const grc = (window as any).grecaptcha
      if (!grc?.render) {
        setTimeout(tryRender, 500)
        return
      }

      try {
        recaptchaWidgetId.current = grc.render(node, {
          sitekey: SITE_KEY,
          callback: (token: string) => { setRecaptchaToken(token) },
          'expired-callback': () => { setRecaptchaToken(null) },
          'error-callback': () => { setRecaptchaToken(null) },
        })
      } catch (e) {
        console.error('reCAPTCHA render error:', e)
      }
    }

    tryRender()
  }, [SITE_KEY])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      if (!recaptchaToken) {
        setSubmitError('Please complete the reCAPTCHA checkbox before submitting.')
        setIsSubmitting(false)
        return
      }

      const token = recaptchaToken

      const packageReference = packageName
        ? `Package Enquiry: ${packageName}${packageUrl ? `\nPackage URL: ${packageUrl}` : ''}`
        : ''
      const userMessage = formData.message.trim()
      const combinedMessage =
        [packageReference, userMessage].filter(Boolean).join('\n\n') ||
        'No additional message provided.'

      const params = new URLSearchParams({
        'lead[first_name]': formData.firstName,
        'lead[last_name]': formData.lastName,
        'lead[email]': formData.email,
        'lead[telephone]': formData.phone,
        'lead[message]': combinedMessage,
        'lead[franchise_id]': API_CONFIG.FRANCHISE_ID || '402',
        'lead[pax]': formData.pax,
        'lead[dates]': formData.dates,
        'lead[origin]': formData.origin,
        'g-recaptcha-response': token,
      })
      if (packageId) {
        params.set('lead[package_id]', packageId)
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/leads?${params.toString()}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        setSubmitSuccess(true)
        setRecaptchaToken(null)
        // Reset the widget so it re-renders when form shows again
        const grc = (window as any).grecaptcha
        if (grc && recaptchaWidgetId.current !== null) {
          grc.reset(recaptchaWidgetId.current)
          recaptchaWidgetId.current = null
        }
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          pax: '',
          dates: '',
          origin: '',
          message: '',
        })
        setTimeout(() => {
          setSubmitSuccess(false)
        }, 5000)
      } else {
        const errorData = await response.text()
        console.error('API Error:', response.status, errorData)
        throw new Error(`API Error: ${response.status}`)
      }
    } catch (error) {
      setSubmitError('Failed to send your message. Please try again.')
      console.error('Contact form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading || !franchiseData) {
    return (
      <div className="bg-indojet-light min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-indojet-light py-24 sm:py-32">
      <Helmet>
        <title>Contact Us | IndoJet Travel</title>
        <meta
          name="description"
          content="Get in touch with IndoJet Travel's expert consultants for tailored holiday planning and support."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/contact' : '/contact'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Us | Chelsea Travel" />
        <meta
          property="og:description"
          content="Speak to our travel experts and plan your perfect trip."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/contact'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact Us',
            url: typeof window !== 'undefined' ? window.location.href : '/contact',
            description:
              'Get in touch with Chelsea Travel’s expert consultants for tailored holiday planning and support.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Contact',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/contact',
              },
            ],
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Contact Our Travel Experts
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Ready to plan your dream vacation? Get in touch with our experienced travel consultants
            who will help create the perfect itinerary for you.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-16 lg:grid-cols-2">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Get in Touch</h3>

            <div className="space-y-6">
              {franchiseData?.telephone && (
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg"
                    style={{ background: '#182c78' }}
                  >
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Phone</h4>
                    <p className="mt-1 text-gray-600">{franchiseData.telephone}</p>
                    {franchiseData.emergency_telephone && (
                      <p className="text-sm text-gray-500">
                        Emergency: {franchiseData.emergency_telephone}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {franchiseData?.email && (
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg"
                    style={{ background: '#182c78' }}
                  >
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Email</h4>
                    <p className="mt-1 text-gray-600">{franchiseData.email}</p>
                    <p className="mt-1 text-gray-600">indojet.team@discovery.co.za</p>
                  </div>
                </div>
              )}

              {franchiseData?.address && (
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg"
                    style={{ background: '#182c78' }}
                  >
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Address</h4>
                    <p className="mt-1 text-gray-600">{franchiseData.address}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-4">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg"
                  style={{ background: '#182c78' }}
                >
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Australia Office details:</h4>
                  <p className="mt-1 text-gray-600">Cell: +61 4 505 87 505</p>
                  <p className="mt-1 text-gray-600">Address: 302/62A Dover Road, Rose Bay, Sydney, Australia</p>
                  <p className="mt-1 text-gray-600">Iata #: 02308250</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg"
                  style={{ background: '#182c78' }}
                >
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Working Hours</h4>
                  <div className="mt-1 text-gray-600">
                    <p>24 Hours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            {(franchiseData?.facebook || franchiseData?.instagram || franchiseData?.whatsapp) && (
              <div className="mt-12">
                <h4 className="font-medium text-gray-900 mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  {franchiseData.facebook && (
                    <a
                      href={franchiseData.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-blue-600"
                    >
                      Facebook
                    </a>
                  )}
                  {franchiseData.instagram && (
                    <a
                      href={franchiseData.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-pink-600"
                    >
                      Instagram
                    </a>
                  )}
                  {franchiseData.whatsapp && (
                    <a
                      href={`https://wa.me/${franchiseData.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-green-600"
                    >
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Image */}
          <div className="flex items-center justify-center">
            <img
              src={publicUrl('Discovery_2.jpg')}
              alt="Discovery Place"
              className="w-full h-full object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        {/* Contact Form - Moved to Bottom */}
        <div className="mx-auto mt-16 max-w-4xl">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Fill In Our Form</h3>
            <p className="text-gray-600">we will get back to you with availability for your next trip!</p>
          </div>

          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h4 className="text-lg font-medium text-green-600 mb-2">
                Message Sent Successfully!
              </h4>
              <p className="text-gray-600">
                Thank you for contacting us. We'll get back to you within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8 bg-indojet-light p-4 sm:p-6 md:p-8 rounded-xl">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-semibold text-gray-900"
                  >
                    First Name <span style={{ color: '#182c78' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78] transition-all duration-200 hover:border-gray-300"
                    placeholder="Enter your first name"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-semibold text-gray-900"
                  >
                    Last Name <span style={{ color: '#182c78' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78] transition-all duration-200 hover:border-gray-300"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900">
                    Email Address <span style={{ color: '#182c78' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78] transition-all duration-200 hover:border-gray-300"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78] transition-all duration-200 hover:border-gray-300"
                  placeholder="+27 12 345 6789"
                />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="pax" className="block text-sm font-semibold text-gray-900">
                    Number of People <span style={{ color: '#182c78' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="pax"
                    id="pax"
                    required
                    value={formData.pax}
                    onChange={handleChange}
                    placeholder="e.g., 2 adults, 1 child"
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78] transition-all duration-200 hover:border-gray-300"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="dates" className="block text-sm font-semibold text-gray-900">
                    Travel Dates <span style={{ color: '#182c78' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="dates"
                    id="dates"
                    required
                    value={formData.dates}
                    onChange={handleChange}
                    placeholder="e.g., December 2024"
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78] transition-all duration-200 hover:border-gray-300"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="origin" className="block text-sm font-semibold text-gray-900">
                  Departure City <span style={{ color: '#182c78' }}>*</span>
                </label>
                <input
                  type="text"
                  name="origin"
                  id="origin"
                  required
                  value={formData.origin}
                  onChange={handleChange}
                  placeholder="e.g., Johannesburg, Cape Town"
                  className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78] transition-all duration-200 hover:border-gray-300"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900">
                  Additional Message
                </label>
                <textarea
                  name="message"
                  id="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your travel preferences, special requirements, or any questions you have..."
                  className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#182c78] focus:border-[#182c78] transition-all duration-200 hover:border-gray-300 resize-none"
                />
              </div>

              <div className="flex justify-center">
                <div ref={recaptchaRefCallback}></div>
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-red-600 text-sm font-medium">{submitError}</p>
                </div>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center justify-center text-lg"
                  style={{
                    backgroundColor: franchiseData?.button_color || '#182c78',
                    borderColor: franchiseData?.button_color || '#182c78',
                  }}
                >
                  <Send className="h-5 w-5 mr-3" />
                  {isSubmitting ? 'Sending Message...' : 'Send Message'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* FAQ Section */}
        <div className="mt-24">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h3>

          <div className="mx-auto max-w-4xl">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  How far in advance should I book?
                </h4>
                <p className="text-gray-600 text-sm">
                  We recommend booking at least 2-3 months in advance for the best availability and
                  prices, especially for peak season travel. However, if your destination requires a
                  visa, you will probably need to book much longer in advance, as some countries are
                  experiencing backlogs in visa applications.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Do you offer travel insurance?</h4>
                <p className="text-gray-600 text-sm">
                  Yes, we can arrange comprehensive travel insurance to protect your trip investment
                  and provide peace of mind. Please note: You are responsible for reading the “fine
                  print” regarding the policy you choose.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Can you create custom packages?</h4>
                <p className="text-gray-600 text-sm">
                  Absolutely! Our travel experts specialize in creating personalized itineraries
                  tailored to your preferences and budget.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  What payment methods do you accept?
                </h4>
                <p className="text-gray-600 text-sm">We accept bank transfers and credit cards.</p>
              </div>
              <div className="text-center md:col-span-2 flex flex-col items-center justify-center">
                <h4 className="font-medium text-gray-900 mb-2">Do you help with visas?</h4>
                <p className="text-gray-600 text-sm">
                  Unfortunately, we are unable to assist with obtaining visas, due to the complex
                  process of navigating different immigration laws and regulations. However, we can
                  recommend a suitable visa service company{' '}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
